import React, {useEffect, useState} from 'react';
import {Search, X} from "react-feather";
import UserAvatar from "../Item/UserAvatar";
import {debounceTime, fromEvent} from "rxjs";
import {getConversationVendorAccounts} from "../../services/api/messaging";
import {ACCOUNT_CHANGE} from "../../services/events";
import LoadSpinner from "../Common/LoadSpinner";
import {BROKER} from "../../services/constants/CompanyType";

const AccountSearch = ({conversation, participants, allVendorAccounts, onAddVendorAccount, onRemoveVendorAccount}) => {
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [conversationVendorAccounts, setConversationVendorAccounts] = useState([]);
    const [notIncludedVendorAccounts, setNotIncludedVendorAccounts] = useState([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState([]);

    useEffect(() => {
        const fetchConversationAccounts = async () => {
            try {
                const includedAccounts = await getConversationVendorAccounts(conversation.id);
                setConversationVendorAccounts(includedAccounts ?? []);
                const includedAccountIds = includedAccounts.map((participant) => participant.id);
                setSelectedAccountIds(includedAccountIds);

                // Map the is_primary property from allVendorAccounts to included accounts
                const includedAccountsWithIsPrimary = includedAccounts.map(account => ({
                    ...account,
                    is_primary: allVendorAccounts.find(vendorAccount => vendorAccount.id === account.id)?.is_primary || false
                }));

                setConversationVendorAccounts(includedAccountsWithIsPrimary);

                // Filter accounts that are not included and map the is_primary property from allVendorAccounts
                const notIncludedAccounts = allVendorAccounts.filter(account => !includedAccountIds.includes(account.id))
                    .map(account => ({
                        ...account,
                        is_primary: account.is_primary || false
                    }));

                setNotIncludedVendorAccounts(notIncludedAccounts);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchConversationAccounts().then(() => {
            setLoading(false);
        });
        const accountChangeObservable = fromEvent(document, ACCOUNT_CHANGE);
        const debouncedAccountChangeObservable = accountChangeObservable.pipe(debounceTime(50));

        const subscription = debouncedAccountChangeObservable.subscribe(() => {
            fetchConversationAccounts().then(() => {
                setLoading(false);
            });
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [allVendorAccounts, conversation.id]);

    useEffect(() => {
        if (inputValue.length >= 3) {
            setLoading(true);
            const filteredAccounts = allVendorAccounts.filter((account) => {
                const fullName = `${account.first_name} ${account.last_name}`.toLowerCase();
                return fullName.includes(inputValue.toLowerCase()) && !selectedAccountIds.includes(account.id);
            });

            setNotIncludedVendorAccounts(filteredAccounts);
            setLoading(false);
        }
    }, [allVendorAccounts, inputValue, selectedAccountIds]);

    const handleInputChange = (event) => {
        const value = event.target.value;
        setInputValue(value);
    };

    const handleAddVendorAccount = async (accountId) => {
        if (loading) {
            return false;
        }
        setLoading(true);
        setInputValue('');
        await onAddVendorAccount(accountId);

        document.dispatchEvent(new Event(ACCOUNT_CHANGE));
    };

    const handleRemoveVendorAccount = async (accountId) => {
        if (loading) {
            return false;
        }
        if (selectedAccountIds.length < 2) {
            return false;
        }
        setLoading(true);
        setInputValue('');
        await onRemoveVendorAccount(accountId);

        document.dispatchEvent(new Event(ACCOUNT_CHANGE));
    };


    return (
        <div
            className="sl-modal-body sl-h-auto sl-px-6 sl-pb-0 sl-pt-24 sl-flex sl-flex-col sl-gap-3">

            <div
                className="sl-flex sl-flex-col sl-gap-6">
                {selectedAccountIds?.length > 0 && <span
                    className="sl-text-gray-900 sl-font-regular sl-text-lg "><span
                    className={"sl-font-medium"}>{selectedAccountIds.length + 1}</span> people are in this conversation.</span>}


                <div className="sl-flex sl-flex-col sl-gap-2 sl-align-stretch">
                    <div
                        className="sl-input sl-border-1 sl-shadow-xs sl-rounded-lg sl-w-fill-available sl-flex">
                        <Search className="sl-icon sl-icon-leading sl-place-self-center sl-h-5 sl-w-5 sl-ml-2"/>
                        <input
                            disabled={selectedAccountIds.length === allVendorAccounts.length}
                            value={inputValue}
                            onChange={handleInputChange}
                            className="sl-input sl-border-none sl-font sl-text-md sl-font sl-font-normal sl-text-md sl-p-0 sl-m-0 sl-h-11 sl-w-fill-available"
                            type="text"
                            placeholder={(selectedAccountIds.length === allVendorAccounts.length) ? 'All accounts are included.' : 'Search for a name to add'}/>
                    </div>
                    {(inputValue.length >= 3) && <div>
                        {notIncludedVendorAccounts && <div
                            className="sl-flex sl-shadow-lg sl-rounded-lg sl-flex-col sl-gap-0 sl-mb-6 sl-text-gray-900 sl-text-md sl-h-auto sl-max-h-80 sl-overflow-y-scroll sl-customized-scrollbar">
                            {notIncludedVendorAccounts.map((account, index) => (
                                <div
                                    key={index}
                                    rel={account.id}
                                    className={`${conversation.archived ? 'sl-cursor-not-allowed' : 'sl-cursor-pointer'} sl-flex sl-justify-flex-start sl-align-center sl-gap-2 sl-py-10 sl-px-14`}
                                    onClick={() => !conversation.archived && handleAddVendorAccount(account.id)}>
                                    <div className="sl-flex sl-avatar-label-group sl-justify-start sl-align-center">
                                        <UserAvatar userData={account} isAvatarActive={false}
                                                    avatarClass="sl-h-8 sl-w-8 sl-inline-flex"/>
                                        <div
                                            className="sl-flex sl-flex-col">
                                            <div className={"sl-flex sl-flex-row"}>
                                            <span
                                                className="sl-font sl-text-md sl-font-medium sl-text-gray-900 sl-mr-1">
                                            {account.first_name} {account.last_name}</span>
                                                {account.is_primary &&
                                                    <div
                                                        className="sl-badge sl-badge-sm sl-badge-gray sl-rounded-2xl sl-text-gray-500 sl-font sl-text-xs sl-font-normal">
                                                        Primary Contact</div>}
                                            </div>
                                            <span
                                                className={"sl-text-gray-500 sl-font sl-text-sm sl-font-normal"}>{conversation.vendor.name}</span>
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>}
                        {notIncludedVendorAccounts.length === 0 &&
                            <div
                                className="sl-font-medium sl-text-md sl-flex sl-align-center sl-justify-center sl-text-gray-700">Not
                                found.</div>}
                        {loading &&
                            <div className="sl-loader sl-mx-auto"/>}
                    </div>}
                </div>

                {loading && <LoadSpinner/>}
                {!loading &&
                    <div className="sl-flex sl-flex-col sl-gap-3 sl-align-center sl-justify-center">
                        {conversationVendorAccounts.length === 0 &&
                            <div className="sl-loader sl-mx-auto"/>}
                        {conversationVendorAccounts.map((account, index) => (
                            <div
                                key={index}
                                rel={account.id}
                                className=' sl-py-2 sl-flex sl-border-bottom-not-last sl-pb-3-not-last sl-rounded-lg sl-w-fill-available sl-justify-space-between sl-align-center'>
                                <div className="sl-flex sl-avatar-label-group sl-justify-start sl-align-center">
                                    <UserAvatar userData={account} isAvatarActive={false}
                                                avatarClass="sl-h-8 sl-w-8 sl-inline-flex"/>

                                    <div
                                        className="sl-flex sl-flex-col">
                                        <div className={"sl-flex sl-flex-row"}>
                                            <span
                                                className="sl-font sl-text-md sl-font-medium sl-text-gray-900 sl-mr-1">
                                            {account.first_name} {account.last_name}</span>
                                            {account.is_primary &&
                                                <div
                                                    className="sl-badge sl-badge-sm sl-badge-gray sl-rounded-2xl sl-text-gray-500 sl-font sl-text-xs sl-font-normal">
                                                    Primary Contact</div>}
                                        </div>
                                        <span
                                            className={"sl-text-gray-500 sl-font sl-text-sm sl-font-normal"}>{conversation.vendor.name}</span>
                                    </div>

                                </div>
                                <div
                                    className={"sl-cursor-pointer sl-btn-clear sl-rounded-lg sl-p-2 sl-h-10 sl-w-10 sl-flex sl-justify-center sl-align-center"}
                                    onClick={() => (!conversation.archived) && handleRemoveVendorAccount(account.id)}>
                                    <X
                                        color={"#667085"} size={20}
                                        className={`${(conversation.archived || loading) ? 'sl-cursor-not-allowed' : 'sl-cursor-pointer'}`}/>
                                </div>
                            </div>))}

                        {(selectedAccountIds?.length > 0) && participants?.filter(participant => participant.type === BROKER).map((account) => {
                            return <div
                                key={conversationVendorAccounts.length + 1}
                                rel={account.id}
                                className=' sl-py-2 sl-flex sl-border-bottom-not-last sl-pb-3-not-last sl-rounded-lg sl-w-fill-available sl-justify-space-between sl-align-center'>
                                <div className="sl-flex sl-avatar-label-group sl-justify-start sl-align-center">
                                    <UserAvatar userData={account} isAvatarActive={false}
                                                avatarClass="sl-h-8 sl-w-8 sl-inline-flex"/>
                                    <div
                                        className="sl-flex sl-flex-col">
                                        <div className={"sl-flex sl-flex-row"}>
                                            <span
                                                className="sl-font sl-text-md sl-font-medium sl-text-gray-900 sl-mr-1">
                                            {account.first_name} {account.last_name}</span>
                                        </div>
                                        <span
                                            className={"sl-text-gray-500 sl-font sl-text-sm sl-font-normal"}>{conversation.broker.name}</span>
                                    </div>
                                </div>
                            </div>
                        })}
                    </div>}
            </div>
        </div>
    );
};

export default AccountSearch;
